import { useState } from 'react';
import { CircularProgress, Grid, TextField, Typography } from '@material-ui/core';
import { SIREN_REGEX } from 'config/constants';
import { useCompany, useCompanyFunctions } from 'contexts/CompanyContext';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import DefaultButton from 'components/DefaultButton';
import DocumentCard from 'components/DocumentCard';
import kbisLogo from '_assets/register/kbis-logo.svg';
import MaiaLogo from 'components/MaiaLogo';
import RegisterLayout from 'pages/RegisterLayout';
import routes from 'config/routes';
import sideImage from '_assets/register/kbis-register.svg';
import useStyles from './styles';

const validationSchema = Yup.object({
  social: Yup.string().required('Champ requis'),
  siren: Yup.string()
    .matches(SIREN_REGEX, 'Veuillez entrer un SIREN valide')
    .required('Champ requis'),
  url: Yup.string(),
}).defined();

const RegisterDocuments = () => {
  const classes = useStyles();
  const history = useHistory();

  const { currentCompany } = useCompany();
  const { editCompany } = useCompanyFunctions();
  const [document, setDocument] = useState({ file: null, url: currentCompany?.kbisURL ?? null });

  const { errors, handleChange, handleSubmit, isSubmitting, touched, values } = useFormik({
    validationSchema,
    initialValues: {
      social: currentCompany?.social_name ?? '',
      siren: currentCompany?.siren ?? '',
      url: currentCompany?.external_rating_url ?? '',
    },
    onSubmit: async ({ social, siren, url }) => {
      if (!currentCompany) return;

      const body = { external_rating_url: url, siren: siren, social_name: social };

      let documentData = null;
      if (document?.file) {
        let formData = new FormData();
        formData.append('file', document?.file);
        documentData = {
          data: formData,
          name: 'kbis',
          url: document?.url,
        };
      }

      try {
        const { edited } = await editCompany(currentCompany, body, documentData);
        if (edited) history.push(routes.home.root);
      } catch (_) {}
    },
  });

  function handleDocumentChange(file, url) {
    setDocument({ file, url });
  }

  function handleCancel() {
    if (isSubmitting) return;
    history.push(routes.home.root);
  }

  const SideComponent = () => (
    <Grid container direction='column' style={{ height: 'auto' }}>
      <Grid container item justifyContent='center'>
        <MaiaLogo />
      </Grid>

      <Grid container item justifyContent='center' style={{ marginTop: '8vh' }}>
        <img src={sideImage} alt='Credit card' width={180} />
      </Grid>

      <Grid container item justifyContent='center' style={{ marginTop: '3vh' }}>
        <Typography className={classes.text} align='center' variant='h3'>
          Votre Kbis c’est la carte d’identité de votre société.
        </Typography>
        <Typography className={classes.text} align='center' variant='h3' style={{ marginTop: '3vh' }}>
          Si nous voulons travailler ensemble il faut que l’on puisse s’assurer que votre
          société existe
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <RegisterLayout SideComponent={<SideComponent />}>
      <Grid container direction='column' style={{ height: 'auto' }}>
        <Grid className={classes.header} container item justifyContent='center'>
          <Grid className={classes.title} container item xs={12} md={9} justifyContent='center'>
            <Typography variant='h1'>Votre Kbis</Typography>
          </Grid>

          <Grid className={classes.title} container item xs={12} md={9} justifyContent='center'>
            <Typography align='center' variant='h2'>
              Confirmez vos informations de société en nous faisant parvenir votre Kbis !
            </Typography>
          </Grid>
        </Grid>

        <Grid container item justifyContent='center' style={{ flexGrow: 1 }}>
          <form
            autoComplete='off'
            className={classes.form}
            onSubmit={handleSubmit}
          >
            <Grid container justifyContent='center' style={{ height: '100%' }}>
              <Grid container item xs={12} md={9} alignItems='center' justifyContent='center' direction='column' spacing={3} style={{ flexGrow: 1 }}>
                <Grid container item>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        error={touched.social && Boolean(errors.social)}
                        fullWidth
                        helperText={touched.social && errors.social}
                        label="Raison sociale"
                        name="social"
                        onChange={handleChange}
                        value={values.social}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        error={touched.siren && Boolean(errors.siren)}
                        fullWidth
                        helperText={touched.siren && errors.siren}
                        label="Siren"
                        name="siren"
                        onChange={handleChange}
                        value={values.siren}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container item>
                  <Grid container item justifyContent='center'>
                    <DocumentCard
                      disabled={isSubmitting}
                      document={document}
                      logo={kbisLogo}
                      name='KBIS'
                      onChange={handleDocumentChange}
                    />
                  </Grid>
                </Grid>

                <Grid container item>
                  <Grid container item justifyContent='center'>
                    <Grid container item xs={12} justifyContent='center'>
                      <Typography align='center' variant='h2'>
                        Améliorez votre visibilité avec votre note Google ou Tripadvisor
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container item>
                  <Grid container item justifyContent='center'>
                    <Grid className={classes.linkField} item xs={12}>
                      <TextField
                        error={touched.url && Boolean(errors.url)}
                        fullWidth
                        helperText={touched.url && errors.url}
                        label="Lien vers mon compte Tripadvisor ou Google"
                        name="url"
                        onChange={handleChange}
                        value={values.url}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container item xs={12} justifyContent='center'>
                <Grid container item xs={12} alignItems='flex-end' justifyContent='center' style={{ marginBottom: '16px' }}>
                  <DefaultButton cancel onClick={handleCancel}>
                    Annuler
                  </DefaultButton>
                  <DefaultButton
                    color='secondary'
                    disabled={isSubmitting}
                    type='submit'
                    variant='contained'
                  >
                    {isSubmitting ? <CircularProgress size={20} /> : 'Valider'}
                  </DefaultButton>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </RegisterLayout>
  );
};

export default RegisterDocuments;
